import { getLocalStorageItem, setLocalStorageItem } from 'app/api/localStorage'
import { PUBLIC_CATALOG_FORM_URL, PUBLIC_CATALOG_SIGNUP } from 'app/constants/urls'
import { getClient } from 'features/common/utils/clientParameter'
import { ConnectionType } from 'features/data-explorer/types'
import { postHubspotRequestSubscriptionForm } from 'features/hubspot/api/hubspot'
import { HubspotForm } from 'features/hubspot/types'
import { sendSQLPostRequest } from 'features/sql/api/sql'
import settings from 'settings'
import {
  CatalogDataset,
  CatalogEntityTypes,
  CatalogSubscription,
  CatalogSubscriptionRequestStatus,
  CatalogSubscriptionType
} from '../types'
import { getRequest } from './request'
import { postRequestStatus } from 'features/common'

export const REQUEST_SUBS = 'CARTO_REQUEST_SUBSCRIPTION_DONE'
const SUBSCRIPTIONS_BASE_URL = `${settings.accountsApiUrl}/do/subscriptions`

// FIXME: Change when new API has support for samples subscriptions
export async function getSubscribedSamples({
  connection = ConnectionType.CartoDw,
  project,
  dataProject,
  dataset,
  refresh
}: {
  connection: string
  project: string
  dataProject: string
  dataset: string
  refresh?: boolean
}) {
  const response = await sendSQLPostRequest({
    refresh,
    connection: connection,
    query: `
          WITH user_samples AS (
            SELECT table_name AS sample_name
            FROM \`${dataProject}.${dataset}.INFORMATION_SCHEMA.TABLES\`
            WHERE \`${project}.carto.__HAS_SAMPLE_PREFIX\`(table_name)
          ),
          result AS (
            SELECT us.sample_name AS dataset_table
            FROM user_samples us
            INNER JOIN \`${project}.carto.spatial_catalog_datasets\` d
              ON (REPLACE(SUBSTRING(d.dataset_id, INSTR(d.dataset_id, '.')+1), '.', '_') = \`${project}.carto.__REMOVE_SAMPLE_PREFIX\`(us.sample_name))
          )
          SELECT * FROM result  ORDER BY dataset_table`,
    extraParams: { client: getClient() }
  })
  return response?.rows?.map((row) => row.dataset_table) || []
}

export async function getActiveSubscriptions(): Promise<CatalogSubscription[]> {
  const subcriptions = await getRequest(SUBSCRIPTIONS_BASE_URL, {}, false)
  return subcriptions?.filter(
    (sub) =>
      !sub.expiresAt ||
      (new Date(sub.expiresAt) > new Date() &&
        [CatalogSubscriptionType.DATA, CatalogSubscriptionType.GEOGRAPHY].includes(sub.datasetType))
  )
}

export async function addSubscriptionRequested({ dataset }: { dataset: CatalogDataset }) {
  const subscriptionsRequested: string[] = getSubscriptionsRequested()
  subscriptionsRequested.push(dataset.id)

  setLocalStorageItem(REQUEST_SUBS, subscriptionsRequested)
}

export async function removeSubscriptionRequested({ dataset }: { dataset: CatalogDataset }) {
  const subscriptionsRequested: string[] = getSubscriptionsRequested()
  const idx = subscriptionsRequested.findIndex((id) => dataset.id === id)
  subscriptionsRequested.splice(idx, 1)

  setLocalStorageItem(REQUEST_SUBS, subscriptionsRequested)
}

export function checkIfDatasetHasBeenRequested(datasetId: string) {
  const subscriptionsRequested: string[] = getSubscriptionsRequested()
  return subscriptionsRequested.some((id) => datasetId === id)
}

export function getSubscriptionsRequested() {
  return getLocalStorageItem(REQUEST_SUBS) || []
}

export async function subscribeSample({ dataset, signal }: { dataset: CatalogDataset; signal?: AbortSignal }) {
  return requestDOSubscriptionAPI({
    type: 'sample',
    source: dataset.sampleInfo?.id as string,
    signal
  }) as Promise<number>
}

export async function subscribePublicDataset({ dataset, signal }: { dataset: CatalogDataset; signal?: AbortSignal }) {
  return requestDOSubscriptionAPI({
    source: dataset?.id as string,
    signal
  }) as Promise<number>
}

export async function subscribePremiumDataset({
  dataset,
  email,
  datasetType,
  requestStatus,
  accountName
}: {
  dataset: CatalogDataset
  requestStatus: CatalogSubscriptionRequestStatus
  email: string
  datasetType: CatalogEntityTypes
  accountName: string
}) {
  return postHubspotRequestSubscriptionForm(
    datasetToHubspotForm({ dataset, datasetType, email, requestStatus, accountName }) as unknown as HubspotForm
  )
}

export async function subscribeDataset({
  dataset,
  email,
  datasetType,
  requestStatus,
  hasCDWAccount,
  accountName
}: {
  dataset: CatalogDataset
  requestStatus: CatalogSubscriptionRequestStatus
  email: string
  datasetType: CatalogEntityTypes
  hasCDWAccount: boolean
  accountName: string
}) {
  return dataset?.licenseId === 'public' && !!hasCDWAccount
    ? subscribePublicDataset({ dataset })
    : subscribePremiumDataset({ dataset, email, datasetType, requestStatus, accountName })
}

export function generateAnonymousSubscriptionUrl(dataset: CatalogDataset) {
  const url = new URL(PUBLIC_CATALOG_FORM_URL)
  url.searchParams.append('category', encodeURI(dataset?.categoryName))
  url.searchParams.append('country', encodeURI(dataset?.countryName))
  url.searchParams.append('datastream', encodeURI(dataset?.name))
  url.searchParams.append('provider', encodeURI(dataset?.providerName))
  url.searchParams.append('license', encodeURI(dataset?.licenseName))
  return url
}
export function generateTryForFreeSubscriptionUrl() {
  return new URL(PUBLIC_CATALOG_SIGNUP)
}

function datasetToHubspotForm({
  dataset,
  datasetType,
  email,
  requestStatus,
  accountName
}: {
  dataset: CatalogDataset
  datasetType: CatalogEntityTypes
  email: string
  requestStatus: CatalogSubscriptionRequestStatus
  accountName: string
}) {
  const spatialDataCatalogPublicUrl = settings.publicCatalogUrl
  return {
    email,
    dataset_id: dataset.id,
    dataset_url: `${spatialDataCatalogPublicUrl}/${datasetType}/${dataset.slug}`,
    datastream_name: dataset.name,
    datastream_request_status: requestStatus,
    country_data: dataset.countryName,
    data_category: dataset.categoryName,
    provider: dataset.providerName,
    carto_3_account_name: accountName
  }
}

async function requestDOSubscriptionAPI({
  type,
  source,
  signal
}: {
  type?: string
  source: string
  signal?: AbortSignal
}) {
  const response = await postRequestStatus(SUBSCRIPTIONS_BASE_URL, { type, source }, { signal })

  if (response !== 201) {
    throw new Error('Cannot subscribe to this dataset. Please try again later.')
  }

  return response
}
